import { Skeleton as MantineSkeleton } from '@mantine/core'
import React from 'react'

interface SkeletonProps {
  height?: number | string
  width?: number | string
  radius?: number | string
  className?: string
  animate?: boolean
}

export function Skeleton({
  height = 20,
  width = '100%',
  radius = 'md',
  className = '',
  animate = true,
}: SkeletonProps) {
  return (
    <MantineSkeleton
      height={height}
      width={width}
      radius={radius}
      className={className}
      animate={animate}
    />
  )
}

// Dashboard specific skeleton components
export function SummaryCardSkeleton() {
  return (
    <div className="bg-white p-4 rounded-lg shadow border-[0.5px] border-gray-80">
      <Skeleton height={16} width="60%" className="mb-2" />
      <Skeleton height={24} width="40%" />
    </div>
  )
}

export function CaseTableRowSkeleton({ page = 'Dashboard' }) {
  return (
    <tr className="border-b border-gray-200">
      <td className="px-4 py-3">
        <Skeleton height={16} width="80%" />
      </td>
      <td className="px-4 py-3">
        <Skeleton height={16} width="90%" />
      </td>
      {page !== 'Dashboard' && (
        <td className="px-4 py-3">
          <Skeleton height={16} width="75%" />
        </td>
      )}
      <td className="px-4 py-3">
        <Skeleton height={16} width="70%" />
      </td>
      {page !== 'Dashboard' && (
        <td className="px-4 py-3">
          <Skeleton height={20} width={80} radius="xl" />
        </td>
      )}
      <td className="px-4 py-3">
        <Skeleton height={16} width="60%" />
      </td>
      {page !== 'ShiftsOverview' && (
        <td className="px-4 py-3">
          <Skeleton height={20} width={60} radius="xl" />
        </td>
      )}
    </tr>
  )
}

export function ResponderTableRowSkeleton() {
  return (
    <tr className="border-b border-gray-200">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Skeleton height={12} width={12} radius="50%" />
          <Skeleton height={16} width="80%" />
        </div>
      </td>
      <td className="px-4 py-3">
        <Skeleton height={16} width="70%" />
      </td>
      <td className="px-4 py-3">
        <Skeleton height={16} width="90%" />
      </td>
      <td className="px-4 py-3">
        <Skeleton height={16} width="60%" />
      </td>
      <td className="px-4 py-3">
        <div className="space-y-1">
          <Skeleton height={16} width="85%" />
          <Skeleton height={12} width="60%" />
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Skeleton height={14} width={14} />
          <Skeleton height={16} width="70%" />
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <Skeleton height={20} width={20} radius="50%" />
      </td>
    </tr>
  )
}

export function ResponderCardSkeleton() {
  return (
    <div className="bg-white p-3 rounded-lg border border-gray-200 flex items-center gap-3">
      <Skeleton height={40} width={40} radius="50%" />
      <div className="flex-1">
        <Skeleton height={14} width="70%" className="mb-1" />
        <Skeleton height={12} width="50%" />
      </div>
    </div>
  )
}

export function ResourceTabSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <ResponderCardSkeleton key={index} />
      ))}
    </div>
  )
}

// Chart skeleton loaders for AdminDashboard
export function ChartSkeleton() {
  return (
    <div className="bg-white p-4 rounded-lg shadow border-[0.5px] border-gray-80 h-full">
      <Skeleton height={20} width="40%" className="mb-4" />
      <div className="space-y-2">
        <Skeleton height={200} width="100%" />
        <div className="flex justify-between items-center mt-2">
          <Skeleton height={12} width="20%" />
          <Skeleton height={12} width="20%" />
        </div>
      </div>
    </div>
  )
}

export function ClockInModalSkeleton() {
  return (
    <div>
      {/* Header Section Skeleton */}
      <div className="animate-pulse">
        <div className="text-sm font-semibold">
          <div className="h-5 w-48 bg-gray-300 rounded" />
        </div>
        <div className="flex justify-between items-center gap-6 mb-6">
          <div className="text-gray-500 text-sm flex-1">
            <div className="h-4 w-full max-w-md bg-gray-300 rounded" />
          </div>
          <div className="h-8 w-36 bg-gray-300 rounded border border-gray-300" />
        </div>

        {/* Search Input Skeleton */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-10 bg-gray-300 rounded border border-gray-300" />
        </div>

        {/* Table Skeleton */}
        <div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="text-left">
                <tr>
                  {[
                    'S/N',
                    'Case ID',
                    'Patient Name',
                    'Location',
                    'Status',
                    'Last Updated',
                    'Diagnosis',
                  ].map((_, idx) => (
                    <th
                      key={idx}
                      className="p-4 text-sm font-medium text-gray-700 whitespace-nowrap"
                    >
                      <div className="h-4 w-16 bg-gray-300 rounded" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Generate 3 skeleton rows */}
                {[1, 2, 3].map((_, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    {/* Main row skeleton */}
                    <tr className="border border-b-0 border-gray-300 rounded-md">
                      {[0, 1, 2, 3, 4, 5, 6].map(cellIndex => (
                        <td key={cellIndex} className="p-4">
                          {cellIndex === 0 && (
                            <div className="h-4 w-6 bg-gray-300 rounded mx-auto" />
                          )}
                          {cellIndex === 1 && (
                            <div className="h-4 w-20 bg-gray-300 rounded" />
                          )}
                          {cellIndex === 2 && (
                            <div className="h-4 w-28 bg-gray-300 rounded" />
                          )}
                          {cellIndex === 3 && (
                            <div className="flex items-center gap-1">
                              <div className="h-4 w-32 bg-gray-300 rounded" />
                              <div className="w-3.5 h-3.5 bg-gray-300 rounded shrink-0" />
                            </div>
                          )}
                          {cellIndex === 4 && (
                            <div className="h-5 w-12 bg-gray-300 rounded" />
                          )}
                          {cellIndex === 5 && (
                            <div className="h-4 w-24 bg-gray-300 rounded" />
                          )}
                          {cellIndex === 6 && (
                            <div className="h-5 w-20 bg-gray-300 rounded-full" />
                          )}
                        </td>
                      ))}
                    </tr>

                    {/* Expandable section skeleton */}
                    <tr className="border border-t-0 border-gray-300 rounded-md">
                      <td colSpan={7} className="px-6 pb-3">
                        <div className="flex flex-col gap-3">
                          {/* Handover note skeleton */}
                          <div className="border-t border-dashed border-gray-200 pt-3">
                            <div className="w-full rounded border border-gray-200 bg-gray-50 px-3 py-2">
                              <div className="flex items-center gap-1.5 mb-1">
                                <div className="h-4 w-24 bg-gray-300 rounded" />
                                <div className="w-3.5 h-3.5 bg-gray-300 rounded" />
                              </div>
                              <div className="h-3 w-full max-w-md bg-gray-300 rounded" />
                            </div>
                          </div>

                          {/* Action buttons skeleton */}
                          <div className="flex justify-end gap-2">
                            <div className="h-7 w-28 bg-gray-300 rounded border border-gray-300" />
                            <div className="h-7 w-32 bg-gray-300 rounded border border-gray-300" />
                            <div className="h-7 w-24 bg-gray-300 rounded border border-gray-300" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Pagination Skeleton */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t">
          <div className="text-sm text-gray-600">
            <div className="h-4 w-64 bg-gray-300 rounded" />
          </div>
          <div className="flex gap-3">
            <div className="h-10 w-24 bg-gray-300 rounded-lg" />
            <div className="h-10 w-24 bg-gray-300 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  )
}

export function IncomingShiftSkeleton() {
  return (
    <div className="text-sm text-gray-900 animate-pulse">
      {/* Dispatcher Section Skeleton */}
      <div className="border-b border-gray-200 pb-3">
        <div className="flex items-center gap-1">
          <div className="h-4 w-20 bg-gray-300 rounded" />
          <div className="h-4 w-32 bg-gray-300 rounded" />
        </div>
      </div>

      {/* Shift Section Skeleton */}
      <div className="border-b border-gray-200 py-3">
        <div className="flex items-center gap-1 mb-3">
          <div className="h-4 w-12 bg-gray-300 rounded" />
          <div className="h-4 w-24 bg-gray-300 rounded" />
          <div className="h-4 w-12 bg-gray-300 rounded" />
        </div>

        <div className="mt-3 flex items-start gap-3">
          {/* Clock icon skeleton */}
          <div className="mt-0.5 h-4 w-4 bg-gray-300 rounded" />

          <div className="space-y-2 flex-1">
            {/* From row skeleton */}
            <div className="flex items-center">
              <div className="h-4 w-12 bg-gray-300 rounded mr-2" />
              <div className="h-4 w-40 bg-gray-300 rounded" />
            </div>

            {/* To row skeleton */}
            <div className="flex items-center">
              <div className="h-4 w-12 bg-gray-300 rounded mr-2" />
              <div className="h-4 w-40 bg-gray-300 rounded" />
            </div>
          </div>
        </div>
      </div>

      {/* Role Section Skeleton */}
      <div className="pt-3">
        <div className="flex items-center gap-1">
          <div className="h-4 w-12 bg-gray-300 rounded" />
          <div className="h-4 w-24 bg-gray-300 rounded" />
        </div>
      </div>
    </div>
  )
}

export function NotificationsModalSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((_, index) => (
        <div
          key={index}
          className="mb-4 border border-[#C6C5D0] rounded overflow-hidden animate-pulse"
        >
          <div className="px-3 py-2">
            {/* Header */}
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-300 rounded" />
                <div className="h-3 w-40 bg-gray-300 rounded mt-2.5" />
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-gray-300" />
                <div className="h-3 w-16 bg-gray-300 rounded" />
              </div>
            </div>

            {/* Summary */}
            <div className="mt-2.5">
              <div className="h-4 w-48 bg-gray-300 rounded mb-1.5" />
              <div className="h-3 w-full bg-gray-300 rounded mb-1" />
              <div className="h-3 w-3/4 bg-gray-300 rounded" />
            </div>

            {/* Activity Bar */}
            <div className="mt-3 px-4 py-2 rounded border border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-16 bg-gray-300 rounded" />
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-gray-300" />
                    <div className="h-3 w-20 bg-gray-300 rounded" />
                  </div>
                  <div className="h-3 w-16 bg-gray-300 rounded" />
                </div>
                <div className="w-4 h-4 bg-gray-300 rounded" />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-4">
              <div className="h-8 w-20 bg-gray-300 rounded" />
              <div className="h-8 w-16 bg-gray-300 rounded" />
              <div className="h-8 w-28 bg-gray-300 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export const MonthlyScheduleGridSkeleton: React.FC<{
  displayedWeeks: Date[][]
  weeksLabels: string[]
  days: string[]
}> = ({ displayedWeeks, weeksLabels, days }) => {
  return (
    <div className="bg-white rounded-2xl p-4 border border-gray-200">
      <div className="overflow-x-auto">
        <div className="min-w-[1000px] animate-pulse">
          {/* Header Row Skeleton */}
          <div
            className={`grid border border-gray-200 rounded-t-lg`}
            style={{
              gridTemplateColumns: `100px repeat(${displayedWeeks.length}, minmax(180px, 1fr))`,
            }}
          >
            <div className="bg-gray-100 font-medium text-sm flex items-center justify-center border-r border-gray-200 min-h-14 rounded-tl-lg">
              <div className="h-4 w-20 bg-gray-300 rounded" />
            </div>

            {weeksLabels.map((_, index) => (
              <div
                key={index}
                className={`font-medium text-xs flex items-center justify-center border-gray-200 min-h-14 text-center ${index === weeksLabels.length - 1 ? '' : 'border-r'}`}
              >
                <div className="h-3 w-24 bg-gray-300 rounded" />
              </div>
            ))}
          </div>

          {/* Body Rows Skeleton */}
          <div>
            {days.map((day, index) => (
              <div
                key={day}
                className={`grid border border-gray-200 border-t-0 ${index === 6 ? 'rounded-b-lg' : ''}`}
                style={{
                  gridTemplateColumns: `100px repeat(${displayedWeeks.length}, minmax(180px, 1fr))`,
                }}
              >
                {/* Day label column */}
                <div
                  className={`sticky left-0 z-10 bg-blue-50 font-semibold text-sm flex items-center justify-center border-r border-gray-200 min-h-[100px] ${index === 6 ? 'rounded-bl-lg' : ''}`}
                >
                  <div className="h-4 w-16 bg-gray-300 rounded" />
                </div>

                {/* Week columns skeleton */}
                {displayedWeeks.map((_, weekIndex) => (
                  <div
                    key={`${day}-${weekIndex}`}
                    className="border-r border-gray-200 min-h-[100px] p-1.5"
                  >
                    <div className="flex flex-col gap-1">
                      {/* Day number skeleton */}
                      <div className="text-right">
                        <div className="h-3 w-6 bg-gray-300 rounded ml-auto" />
                      </div>

                      {/* Shift card skeletons - show 1 placeholder cards */}
                      <div className="space-y-1.5 mt-1">
                        <div className="h-20 bg-gray-100 rounded-md" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
